import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

const LOGO = '/assets/inspect-canada-logo.png';
const FOOTER_LOGO = '/assets/inspect-canada-logo.png';
const PHONE = '778-877-7540';
const EMAIL = 'fastreply@inspect.ca';
const WP = 'https://inspect.ca';
const SCHEDULE_URL = `${WP}/schedule-inspection/`;

const inspectionLinks = [
  ['Home Inspection', `${WP}/home-inspection/`],
  ['Townhouse Inspection', `${WP}/townhouse-inspection/`],
  ['Condo Inspection', `${WP}/condo-inspection/`],
  ['Pre-Listing Inspection', `${WP}/seller-inspections/`],
  ['Drain Inspection', `${WP}/drain-inspection/`],
];

const airLinks = [
  ['Mold Inspection', `${WP}/mold-inspection/`],
  ['Radon Gas Inspection', `${WP}/radon/`],
];

function SvgIcon({ children, size = 29 }: { children: ReactNode; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}

const icons = {
  inspection: <SvgIcon><path d="M9 5h6M9 3h6v4H9zM7 5H5v16h14V5h-2M8 12l2 2 5-5M8 18h8" /></SvgIcon>,
  air: <SvgIcon><circle cx="12" cy="12" r="9" /><circle cx="12" cy="7" r="1" /><circle cx="8" cy="11" r="1" /><circle cx="15.5" cy="11" r="1" /><circle cx="11" cy="16" r="1" /><path d="M15 15l1.5 1.5" /></SvgIcon>,
  johnni: <SvgIcon><path d="M12 2l1.3 4.2L17.5 7.5l-4.2 1.3L12 13l-1.3-4.2-4.2-1.3 4.2-1.3zM19 13l.8 2.2L22 16l-2.2.8L19 19l-.8-2.2L16 16l2.2-.8z" /></SvgIcon>,
  team: <SvgIcon><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M17 11a3 3 0 1 0 0-6M22 21v-2a4 4 0 0 0-3-3.87" /></SvgIcon>,
  realtor: <SvgIcon><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M3 12h18M10 12v2h4v-2" /></SvgIcon>,
  contact: <SvgIcon><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 2 .7 2.8a2 2 0 0 1-.5 2.1L8.1 9.9a16 16 0 0 0 6 6l1.3-1.2a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2z" /></SvgIcon>,
};

function DesktopDropdown({ label, icon, links }: { label: string; icon: ReactNode; links: string[][] }) {
  return (
    <details className="group relative">
      <summary className="flex min-w-[105px] cursor-pointer list-none flex-col items-center gap-2 rounded-xl px-3 py-2 text-sm font-bold text-navy hover:bg-slate-50 hover:text-accent">
        {icon}
        <span className="flex items-center gap-1">{label}<span className="text-xs transition group-open:rotate-180">⌄</span></span>
      </summary>
      <div className="absolute left-1/2 top-[5.7rem] z-50 w-72 -translate-x-1/2 rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl">
        <p className="px-3 pb-2 pt-1 text-[11px] font-extrabold uppercase tracking-[0.14em] text-accent">{label}</p>
        {links.map(([text, href]) => <a key={href} href={href} className="block rounded-xl px-3 py-3 text-sm font-semibold text-slate-700 hover:bg-red-50 hover:text-accent">{text}</a>)}
      </div>
    </details>
  );
}

function MobileSection({ label, links }: { label: string; links: string[][] }) {
  return (
    <details className="border-b border-slate-100">
      <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between px-4 py-3 font-bold text-navy">
        {label}<span aria-hidden="true">⌄</span>
      </summary>
      <div className="bg-slate-50 px-3 py-2">
        {links.map(([text, href]) => <a key={href} href={href} className="block min-h-11 rounded-lg px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-white hover:text-accent">{text}</a>)}
      </div>
    </details>
  );
}

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-slate-200 bg-white shadow-sm">
      <div className="hidden bg-[#08162d] text-white min-[1180px]:block">
        <div className="mx-auto flex h-12 max-w-[104.5rem] items-center justify-between gap-5 px-5 text-[13px] font-semibold">
          <div className="flex items-center gap-6">
            <a href={`tel:${PHONE}`} className="hover:text-red-200">☎ {PHONE}</a>
            <span>♢ CPBC 70020</span>
            <a href={`${WP}/inspectors/`} className="hover:text-red-200">Inspector Training</a>
            <a href={`${WP}/blog/`} className="hover:text-red-200">Blog</a>
            <a href={`${WP}/contact/`} className="hover:text-red-200">Service Area</a>
            <a href={`${WP}/vancouver-home-inspection-reviews/`} className="hover:text-red-200">★ 4,000+ 5-Star Reviews</a>
          </div>
          <div className="flex items-center gap-4" aria-label="Social media">
            <a href="https://www.facebook.com/InspectCanada" aria-label="Inspect Canada on Facebook" className="font-black hover:text-red-200">f</a>
            <a href="https://www.instagram.com/inspectcanada/" aria-label="Inspect Canada on Instagram" className="hover:text-red-200">◎</a>
            <a href="https://www.youtube.com/@inspectcanada" aria-label="Inspect Canada on YouTube" className="hover:text-red-200">▶</a>
          </div>
        </div>
      </div>

      <div className="hidden min-[1180px]:block">
        <div className="mx-auto flex h-28 max-w-[104.5rem] items-center gap-6 px-5">
          <a href={WP} aria-label="Inspect Canada home" className="w-[10.2%] shrink-0">
            <img src={LOGO} alt="Inspect Canada" className="h-[5.6rem] max-w-full object-contain" />
          </a>
          <nav className="flex min-w-0 flex-1 items-center justify-between" aria-label="Primary navigation">
            <DesktopDropdown label="Inspection" icon={icons.inspection} links={inspectionLinks} />
            <DesktopDropdown label="Indoor Air Quality" icon={icons.air} links={airLinks} />
            <a href={`${WP}/johnni/`} className="flex min-w-[88px] flex-col items-center gap-2 rounded-xl px-3 py-2 text-sm font-bold text-accent hover:bg-red-50">{icons.johnni}Johnni</a>
            <Link to="/inspection-team" className="flex min-w-[88px] flex-col items-center gap-2 rounded-xl bg-red-50 px-3 py-2 text-sm font-bold text-accent">{icons.team}Our Team</Link>
            <a href={`${WP}/realtors/`} className="flex min-w-[88px] flex-col items-center gap-2 rounded-xl px-3 py-2 text-sm font-bold text-navy hover:bg-slate-50 hover:text-accent">{icons.realtor}Realtor</a>
            <a href={`${WP}/contact/`} className="flex min-w-[88px] flex-col items-center gap-2 rounded-xl px-3 py-2 text-sm font-bold text-navy hover:bg-slate-50 hover:text-accent">{icons.contact}Contact Us</a>
          </nav>
          <span className="h-14 w-px shrink-0 bg-slate-300" aria-hidden="true" />
          <a href={SCHEDULE_URL} className="inline-flex min-h-14 w-[14.5%] min-w-[180px] items-center justify-center rounded-xl bg-accent px-5 font-extrabold uppercase text-white shadow-md hover:bg-accent-dark">Get a Quote</a>
        </div>
      </div>

      <div className="min-[1180px]:hidden">
        <div className="flex h-[74px] items-center justify-between gap-2 px-3 sm:px-5">
          <button type="button" onClick={() => setMobileOpen((open) => !open)} aria-expanded={mobileOpen} aria-label={mobileOpen ? 'Close navigation menu' : 'Open navigation menu'} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-slate-300 text-2xl text-navy">
            <span aria-hidden="true">{mobileOpen ? '×' : '☰'}</span>
          </button>
          <a href={WP} aria-label="Inspect Canada home"><img src={LOGO} alt="Inspect Canada" className="h-[58px] w-auto" /></a>
          <a href={SCHEDULE_URL} className="inline-flex min-h-11 shrink-0 items-center rounded-xl bg-accent px-3 text-xs font-extrabold uppercase text-white sm:px-5 sm:text-sm">Get a Quote</a>
        </div>
        <div className={`overflow-y-auto border-t border-slate-200 bg-white transition-[max-height] duration-300 ${mobileOpen ? 'max-h-[calc(100vh-74px)]' : 'max-h-0'}`}>
          <nav aria-label="Mobile navigation" className="mx-auto max-w-xl pb-5">
            <MobileSection label="Inspection" links={inspectionLinks} />
            <MobileSection label="Indoor Air Quality" links={airLinks} />
            <a href={`${WP}/johnni/`} className="block min-h-12 border-b border-slate-100 px-4 py-3 font-bold text-accent">Johnni</a>
            <Link to="/inspection-team" className="block min-h-12 border-b border-slate-100 bg-red-50 px-4 py-3 font-bold text-accent">Our Team</Link>
            <a href={`${WP}/realtors/`} className="block min-h-12 border-b border-slate-100 px-4 py-3 font-bold text-navy">Realtor</a>
            <a href={`${WP}/contact/`} className="block min-h-12 border-b border-slate-100 px-4 py-3 font-bold text-navy">Contact Us</a>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 px-4 pt-4 text-sm font-semibold text-slate-600">
              <a href={`${WP}/inspectors/`} className="min-h-11 py-3">Inspector Training</a>
              <a href={`${WP}/blog/`} className="min-h-11 py-3">Blog</a>
              <a href={`${WP}/contact/`} className="min-h-11 py-3">Service Area</a>
              <a href={`${WP}/vancouver-home-inspection-reviews/`} className="min-h-11 py-3">Reviews</a>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
}

export function PageHero({ eyebrow, title, subtitle, breadcrumb, aside }: {
  eyebrow?: string;
  title: ReactNode;
  subtitle?: ReactNode;
  breadcrumb?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <section className="relative overflow-hidden bg-navy text-white">
      <div className="absolute inset-0 opacity-25" style={{ background: 'radial-gradient(circle at 18% 25%, #1769b0 0, transparent 45%), radial-gradient(circle at 82% 75%, #B10000 0, transparent 42%)' }} />
      <div className={`relative mx-auto max-w-7xl px-5 py-14 md:py-20 ${aside ? 'grid items-center gap-10 lg:grid-cols-3' : ''}`}>
        <div className={aside ? 'lg:col-span-2' : ''}>
          {breadcrumb ? <nav className="mb-3 text-sm text-slate-400">{breadcrumb}</nav> : null}
          {eyebrow ? <p className="text-xs font-semibold uppercase tracking-[0.2em] text-red-300">{eyebrow}</p> : null}
          <h1 className="mt-2 max-w-3xl text-3xl font-extrabold leading-tight md:text-5xl">{title}</h1>
          {subtitle ? <p className="mt-4 max-w-2xl text-lg text-slate-300">{subtitle}</p> : null}
        </div>
        {aside ? <div className="lg:col-span-1">{aside}</div> : null}
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="bg-navy text-slate-300">
      <div className="mx-auto grid max-w-7xl gap-10 px-5 py-14 md:grid-cols-4">
        <div>
          <img src={FOOTER_LOGO} alt="Inspect Canada" className="mb-4 h-14 w-auto" />
          <p className="text-sm leading-relaxed text-slate-400">Canada's highest-rated home inspection company, serving Metro Vancouver and the Lower Mainland, 7 days a week.</p>
        </div>
        <div>
          <h4 className="mb-3 font-semibold text-white">Inspections</h4>
          <ul className="space-y-2 text-sm">
            <li><a href={`${WP}/home-inspection/`} className="hover:text-white">Home Inspection</a></li>
            <li><a href={`${WP}/condo-inspection/`} className="hover:text-white">Condo Inspection</a></li>
            <li><a href={`${WP}/townhouse-inspection/`} className="hover:text-white">Townhouse Inspection</a></li>
            <li><a href={`${WP}/seller-inspections/`} className="hover:text-white">Pre-Listing Inspection</a></li>
            <li><a href={`${WP}/mold-inspection/`} className="hover:text-white">Mold Inspection</a></li>
            <li><a href={`${WP}/radon/`} className="hover:text-white">Radon Gas Inspection</a></li>
          </ul>
        </div>
        <div>
          <h4 className="mb-3 font-semibold text-white">Company</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/inspection-team" className="hover:text-white">Our Team</Link></li>
            <li><a href={`${WP}/realtors/`} className="hover:text-white">For Realtors</a></li>
            <li><a href={`${WP}/inspectors/`} className="hover:text-white">Inspector Training</a></li>
            <li><a href={`${WP}/blog/`} className="hover:text-white">Blog</a></li>
            <li><a href={`${WP}/contact/`} className="hover:text-white">Service Area</a></li>
            <li><a href={`${WP}/vancouver-home-inspection-reviews/`} className="hover:text-white">Reviews</a></li>
            <li><a href={`${WP}/contact/`} className="hover:text-white">Contact Us</a></li>
          </ul>
        </div>
        <div>
          <h4 className="mb-3 font-semibold text-white">Get in touch</h4>
          <ul className="space-y-2 text-sm text-slate-400">
            <li>📞 <a href={`tel:${PHONE}`} className="hover:text-white">{PHONE}</a></li>
            <li>✉️ <a href={`mailto:${EMAIL}`} className="hover:text-white">{EMAIL}</a></li>
            <li>📍 200 – 100 Park Royal, West Vancouver, BC</li>
          </ul>
          <a href={SCHEDULE_URL} className="mt-4 inline-block rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-dark">Book online</a>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col justify-between gap-2 px-5 py-5 text-xs text-slate-500 sm:flex-row">
          <span>© {new Date().getFullYear()} In Spect Building Consultants Inc. dba Inspect Canada. All rights reserved.</span>
          <span><a href={`${WP}/privacy-policy/`} className="hover:text-slate-300">Privacy</a> · <a href={`${WP}/terms-and-conditions/`} className="hover:text-slate-300">Terms</a></span>
        </div>
      </div>
    </footer>
  );
}

export function Stars({ className = '' }: { className?: string }) {
  return <span className={`text-amber-400 tracking-tight ${className}`} aria-hidden>★★★★★</span>;
}
